import { useState, useEffect, useRef, useCallback } from 'react';
import { OnboardingFormData, FormStep } from '../types';
import { UploadedFileInfo } from '../services/api';

// Prefixo para chaves no localStorage
const STORAGE_KEY_PREFIX = 'tcm_draft_';

// Intervalo de auto-save (30 segundos)
const AUTO_SAVE_INTERVAL_MS = 30_000;

interface DraftData {
  formData: Partial<OnboardingFormData>;
  currentStep: FormStep;
  uploadedFiles: Record<string, UploadedFileInfo>;
  savedAt: string;
}

interface UseDraftPersistenceOptions {
  token: string | null;
  formData: Partial<OnboardingFormData>;
  currentStep: FormStep;
  uploadedFiles: Record<string, UploadedFileInfo>;
  onDraftLoaded?: (data: {
    formData: Partial<OnboardingFormData>;
    currentStep: FormStep;
  }) => void;
}

interface UseDraftPersistenceReturn {
  saveDraft: () => void;
  saveDraftToFile: () => void;
  loadDraftFromFile: () => void;
  isSaving: boolean;
  lastSavedAt: string | null;
  draftError: string | null;
  clearDraft: () => void;
}

function getStorageKey(token: string): string {
  const shortId = token.slice(-16);
  return `${STORAGE_KEY_PREFIX}${shortId}`;
}

const VALID_STEPS: FormStep[] = [
  'identification',
  'address',
  'commercial-address',
  'professional',
  'financial',
  'shareholders',
  'beneficiaries',
  'credit-questionnaire',
  'authorizations',
  'documents',
  'review',
];

/**
 * Hook de persistência de rascunho via localStorage.
 * Salva automaticamente a cada 30s quando dados mudam.
 * Carrega rascunho existente ao montar.
 */
export function useDraftPersistence({
  token,
  formData,
  currentStep,
  uploadedFiles,
  onDraftLoaded,
}: UseDraftPersistenceOptions): UseDraftPersistenceReturn {
  const [isSaving, setIsSaving] = useState(false);
  const [lastSavedAt, setLastSavedAt] = useState<string | null>(null);
  const [draftError, setDraftError] = useState<string | null>(null);

  // Refs para auto-save (evitam recriar o interval a cada render)
  const formDataRef = useRef(formData);
  const currentStepRef = useRef(currentStep);
  const uploadedFilesRef = useRef(uploadedFiles);
  const prevSnapshotRef = useRef<string>('');
  const onDraftLoadedRef = useRef(onDraftLoaded);

  // Manter refs atualizados
  useEffect(() => { formDataRef.current = formData; }, [formData]);
  useEffect(() => { currentStepRef.current = currentStep; }, [currentStep]);
  useEffect(() => { uploadedFilesRef.current = uploadedFiles; }, [uploadedFiles]);
  useEffect(() => { onDraftLoadedRef.current = onDraftLoaded; }, [onDraftLoaded]);

  // Salvar rascunho no localStorage
  const saveDraft = useCallback(() => {
    if (!token) return;

    setIsSaving(true);
    setDraftError(null);

    try {
      const draft: DraftData = {
        formData: formDataRef.current,
        currentStep: currentStepRef.current,
        uploadedFiles: uploadedFilesRef.current,
        savedAt: new Date().toISOString(),
      };

      localStorage.setItem(getStorageKey(token), JSON.stringify(draft));
      setLastSavedAt(draft.savedAt);
    } catch {
      setDraftError('draftLocalSaveError');
    } finally {
      setIsSaving(false);
    }
  }, [token]);

  // Limpar rascunho (após submissão bem-sucedida)
  const clearDraft = useCallback(() => {
    if (!token) return;
    try {
      localStorage.removeItem(getStorageKey(token));
    } catch {
      // Silencioso
    }
  }, [token]);

  // Carregar rascunho existente ao montar
  useEffect(() => {
    if (!token) return;

    try {
      const stored = localStorage.getItem(getStorageKey(token));
      if (!stored) return;

      const draft: DraftData = JSON.parse(stored);

      const restoredStep = VALID_STEPS.includes(draft.currentStep)
        ? draft.currentStep
        : 'identification';

      onDraftLoadedRef.current?.({
        formData: draft.formData,
        currentStep: restoredStep,
      });

      setLastSavedAt(draft.savedAt);
    } catch {
      // JSON inválido ou localStorage corrompido — ignorar
    }
  }, [token]);

  // Auto-save a cada 30s quando dados mudaram
  useEffect(() => {
    if (!token) return;

    const timer = setInterval(() => {
      const snapshot = JSON.stringify(formDataRef.current);
      if (snapshot !== prevSnapshotRef.current) {
        prevSnapshotRef.current = snapshot;
        saveDraft();
      }
    }, AUTO_SAVE_INTERVAL_MS);

    return () => clearInterval(timer);
  }, [token, saveDraft]);

  // Salvar rascunho como arquivo .txt (JSON)
  const saveDraftToFile = useCallback(() => {
    try {
      const draft: DraftData = {
        formData: formDataRef.current,
        currentStep: currentStepRef.current,
        uploadedFiles: uploadedFilesRef.current,
        savedAt: new Date().toISOString(),
      };

      const json = JSON.stringify(draft, null, 2);
      const blob = new Blob([json], { type: 'text/plain;charset=utf-8' });
      const url = URL.createObjectURL(blob);

      try {
        const date = new Date().toISOString().slice(0, 10);
        const a = document.createElement('a');
        a.href = url;
        a.download = `rascunho_tcm_${date}.txt`;
        document.body.appendChild(a);
        a.click();
        document.body.removeChild(a);
      } finally {
        URL.revokeObjectURL(url);
      }
    } catch {
      setDraftError('draftSaveError');
    }
  }, []);

  // Carregar rascunho a partir de arquivo .txt
  const loadDraftFromFile = useCallback(() => {
    const input = document.createElement('input');
    input.type = 'file';
    input.accept = '.txt';

    input.onchange = (e: Event) => {
      const file = (e.target as HTMLInputElement).files?.[0];
      if (!file) return;

      const reader = new FileReader();
      reader.onload = (ev) => {
        try {
          const text = ev.target?.result as string;
          const draft: DraftData = JSON.parse(text);

          // Validar estrutura mínima
          if (!draft.formData || !draft.currentStep) {
            setDraftError('draftFileInvalid');
            return;
          }

          const restoredStep = VALID_STEPS.includes(draft.currentStep)
            ? draft.currentStep
            : 'identification';

          onDraftLoadedRef.current?.({
            formData: draft.formData,
            currentStep: restoredStep,
          });

          setLastSavedAt(draft.savedAt);
          setDraftError(null);
        } catch {
          setDraftError('draftLoadError');
        }
      };
      reader.onerror = () => {
        setDraftError('draftLoadError');
      };
      reader.readAsText(file);
    };

    input.click();
  }, []);

  return { saveDraft, saveDraftToFile, loadDraftFromFile, isSaving, lastSavedAt, draftError, clearDraft };
}
